import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { normalizeUrl } from "../../utils/helper";
import { syncIssueStickyTop, resetIssueStickyTop } from "../../utils/stickyIssueHeader";
import { useAuth } from "../modals/AuthContext";
import { trackEvent, AnalyticsEvents } from "../../utils/analytics";
import { Monitor, Smartphone } from "lucide-react";

const DEVICE_STRATEGIES = [
  { id: "desktop", label: "Desktop", icon: Monitor },
  { id: "mobile", label: "Mobile", icon: Smartphone },
];

const AnalyzeForm = ({
  setMetrics,
  loading,
  setLoading,
  progress,
  setProgress,
  activeStrategy,
  setActiveStrategy,
  setTaskId = () => {},
  onRunTest,
  setErrors,
  errors, // Add errors to props
  startPolling,
  url,
  setUrl,
  onClearAll,
  hasRunTest, // Add hasRunTest prop
  aiModel,
}) => {
  const { token } = useAuth();
  const [error, setError] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const abortControllers = useRef({
    desktop: null,
    mobile: null,
  });
  const strategySelector = useRef(null);
  const [selectorPosition, setSelectorPosition] = useState({
    fixed: false,
    top: 0,
    showBg: false,
  });

  // Track current task IDs using ref
  const currentTaskIds = useRef({
    desktop: null,
    mobile: null,
  });
  const testGroupIdRef = useRef(null);

  const [isSuggestionsDisabledByAdmin, setIsSuggestionsDisabledByAdmin] =
    useState(false);

  useEffect(() => {
    axios
      .get(`${import.meta.env.VITE_BACKEND_URL}/api/metrics/app-settings`)
      .then((res) => {
        if (res.data?.isSuggestionsDisabledByAdmin) {
          setIsSuggestionsDisabledByAdmin(true);
        }
      })
      .catch(() => {});
  }, []);

  const fetchMetrics = useCallback(
    async (url, strategy, taskId) => {
      // Create new AbortController for this request
      const controller = new AbortController();
      abortControllers.current[strategy] = controller;

      // Store the current task ID
      currentTaskIds.current[strategy] = taskId;

      setTaskId(taskId);
      setLoading((prev) => ({ ...prev, [strategy]: true }));
      setProgress((prev) => ({ ...prev, [strategy]: 0 }));
      setErrors((prev) => ({ ...prev, [strategy]: null }));

      // Start polling for progress updates
      if (startPolling) {
        startPolling(taskId, strategy);
      }

      try {
        const headers = {
          "X-PageTest-Client-Host": window.location.hostname,
        };
        if (token) headers.Authorization = `Bearer ${token}`;

        const enableSuggestions = !isSuggestionsDisabledByAdmin;

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_URL}/api/metrics/analyze`,
          {
            url,
            strategy,
            enableSuggestions,
            aiModel,
            taskId,
            testGroupId: testGroupIdRef.current,
          },
          {
            signal: controller.signal,
            withCredentials: true,
            headers,
          }
        );

        if (response.data && response.data.data) {
          const data = response.data.data;
          setMetrics((prev) => ({
            ...prev,
            [strategy]: {
              ...data,
              testGroupId: testGroupIdRef.current,
              enableSuggestions,
              isSuggestionsDisabledByAdmin,
            },
          }));
          trackEvent(AnalyticsEvents.TEST_COMPLETED, {
            device: strategy,
            url: data.url || url,
          });
          // Update displayed URL to actual browsed URL (e.g. https://www.seelay.in/ after redirect)
          if (data.url) {
            setUrl(data.url);
          }
          return data;
        }
        return null;
      } catch (err) {
        if (err.name === "CanceledError" || err.message === "canceled") {
          return null;
        }
        console.error(`Error fetching ${strategy} metrics:`, err.message);
        setErrors((prev) => ({
          ...prev,
          [strategy]: err.message || "Failed to load metrics",
        }));
        trackEvent(AnalyticsEvents.TEST_FAILED, { device: strategy, url });
        toast.error(`Failed to fetch ${strategy} metrics`);
        return null;
      } finally {
        setLoading((prev) => ({ ...prev, [strategy]: false }));
        abortControllers.current[strategy] = null; // Clean up
      }
    },
    [
      isSuggestionsDisabledByAdmin,
      aiModel,
      token,
      setMetrics,
      setLoading,
      setTaskId,
      setErrors,
      startPolling,
      setUrl,
    ]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrors({ desktop: null, mobile: null });

    const normalizedUrl = normalizeUrl(url);
    // Auto-add https:// to display if user entered domain without protocol
    if (url?.trim() && !/^https?:\/\//i.test(url.trim())) {
      setUrl(normalizedUrl);
    }

    // Cancel any existing requests
    Object.entries(abortControllers.current).forEach(
      ([strategy, controller]) => {
        if (controller) {
          controller.abort();
          setLoading((prev) => ({ ...prev, [strategy]: false }));
        }
      }
    );

    if (onRunTest) onRunTest();

    const testGroupId = uuidv4();
    testGroupIdRef.current = testGroupId;

    trackEvent(AnalyticsEvents.TEST_STARTED, { url: normalizedUrl });

    // Generate new task IDs using uuidv4 for uniqueness
    const desktopTaskId = `desktop-${uuidv4()}`;
    const mobileTaskId = `mobile-${uuidv4()}`;

    try {
      const desktopPromise = fetchMetrics(
        normalizedUrl,
        "desktop",
        desktopTaskId,
      );

      // Stagger mobile start to avoid bursting PageSpeed + Groq rate limits
      await new Promise((resolve) => setTimeout(resolve, 3000));

      const mobilePromise = fetchMetrics(
        normalizedUrl,
        "mobile",
        mobileTaskId,
      );

      await Promise.all([desktopPromise, mobilePromise]);
    } catch (err) {
      if (err.name !== "CanceledError") {
        console.error("Error analyzing website:", err.message);
        setError("Failed to analyze website");
        toast.error("Failed to analyze website. Please try again.");
      }
    }
  };

  // Combined online/offline handler with request cancellation
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true); // Original functionality
      toast.success("You're back online!"); // toast
    };

    const handleOffline = () => {
      setIsOnline(false); // Original functionality

      // New: Cancel all ongoing requests when going offline
      Object.entries(abortControllers.current).forEach(
        ([strategy, controller]) => {
          if (controller) {
            controller.abort();
            setErrors((prev) => ({
              ...prev,
              [strategy]:
                "You're offline. Please check your internet connection.",
            }));
            setLoading((prev) => ({ ...prev, [strategy]: false }));
          }
        }
      );

      toast.error("You're offline. Please check your internet connection."); // toast
    };

    // Original event listeners
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);

      // New: Clean up any pending requests when component unmounts
      Object.values(abortControllers.current).forEach((controller) => {
        if (controller) controller.abort();
      });
    };
  }, []); // Keep empty dependency array as in original

  useEffect(() => {
    const navElement = document.getElementById("navbar");
    const selectorEl = strategySelector.current;

    if (!navElement || !selectorEl) return;

    const storeInitialTop = () => {
      const rect = selectorEl.getBoundingClientRect();
      selectorEl.dataset.initialTop = String(rect.top + window.scrollY);
    };

    storeInitialTop();

    const updatePosition = () => {
      if (!selectorEl || !navElement) return;

      const initialTop = parseFloat(selectorEl.dataset.initialTop);
      const navHeight = navElement.offsetHeight;
      const scrollY = window.scrollY;
      const threshold = initialTop - navHeight - 20;
      const isFixed = scrollY > threshold;

      const bar = selectorEl.querySelector("[data-switcher-bar]");
      const barBottom = bar?.getBoundingClientRect().bottom ?? 0;
      const firstIssue = document.querySelector("[data-issue-card]");
      const showBg =
        isFixed &&
        firstIssue != null &&
        firstIssue.getBoundingClientRect().top <= barBottom;

      if (isFixed) {
        setSelectorPosition({
          fixed: true,
          top: navHeight + 4,
          showBg,
        });
      } else {
        setSelectorPosition({
          fixed: false,
          top: 0,
          showBg: false,
        });
        storeInitialTop();
      }

      syncIssueStickyTop(navElement, selectorEl, isFixed);

      if (bar) {
        selectorEl.style.minHeight = `${bar.offsetHeight}px`;
      }
    };

    const observer = new ResizeObserver(() => {
      updatePosition();
    });
    observer.observe(navElement);
    observer.observe(selectorEl);

    const bar = selectorEl.querySelector("[data-switcher-bar]");
    if (bar) observer.observe(bar);

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updatePosition();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", updatePosition, { passive: true });

    const initTimer = setTimeout(updatePosition, 100);

    return () => {
      clearTimeout(initTimer);
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updatePosition);
      resetIssueStickyTop();
    };
  }, [hasRunTest]);

  return (
    <motion.div
      className="w-full h-full"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {!isOnline && (
        <p className="text-red-500 text-center font-bold mb-4">
          You're offline. Please connect to the internet to run the test.
        </p>
      )}

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4 max-w-2xl mx-auto"
      >
        <div className="flex items-stretch gap-3 sm:gap-4 w-full">
          <div className="relative w-full min-w-0">
            <input
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onBlur={() => {
                if (url?.trim() && !/^https?:\/\//i.test(url.trim())) {
                  setUrl(normalizeUrl(url));
                }
              }}
              placeholder="https://example.com"
              required
              name="url"
              autoComplete="url"
              disabled={!isOnline || loading.desktop || loading.mobile}
              className={`h-11 border border-input rounded-xl px-4 w-full pr-10 text-base bg-background
                ${
                  !isOnline || loading.desktop || loading.mobile
                    ? "bg-muted opacity-60"
                    : "hover:border-foreground/30 focus:border-foreground"
                }
                focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-200`}
            />

            {url && (
              <button
                type="button"
                onClick={onClearAll}
                disabled={loading.desktop || loading.mobile}
                className="absolute right-2 top-2/4 transform -translate-y-2/4 text-muted-foreground hover:text-foreground focus:outline-none"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            )}
          </div>

          <button
            type="submit"
            disabled={!isOnline || loading.desktop || loading.mobile}
            className="h-11 px-5 shrink-0 inline-flex items-center justify-center rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 focus:outline-none focus:ring-2 focus:ring-ring disabled:bg-muted disabled:text-muted-foreground whitespace-nowrap"
          >
            Run Test
          </button>
        </div>

        {/* Placeholder div to maintain layout when selector becomes fixed */}
        {hasRunTest && (
          <div ref={strategySelector} className="relative mt-1 mb-8">
            <div
              data-switcher-fixed
              className={
                selectorPosition.fixed
                  ? "fixed left-0 right-0 z-[25]"
                  : undefined
              }
              style={
                selectorPosition.fixed
                  ? { top: `${selectorPosition.top}px` }
                  : undefined
              }
            >
              <div className="px-6">
                <div className="mx-auto max-w-3xl px-4 sm:px-5">
                  <div
                    data-switcher-bar
                    className={`flex items-center justify-center py-1.5 ${
                      selectorPosition.fixed && selectorPosition.showBg
                        ? "switcher-bar-mask"
                        : ""
                    }`}
                  >
                    <nav
                      role="tablist"
                      aria-label="Device type"
                      className="relative z-10 inline-flex rounded-2xl border border-white/60 bg-white/45 backdrop-blur-2xl backdrop-saturate-150 shadow-sm ring-1 ring-black/[0.04] p-1.5 gap-1.5"
                    >
                {DEVICE_STRATEGIES.map(({ id, label, icon: Icon }) => {
                  const isActive = activeStrategy === id;
                  const isLoading = loading[id];
                  const hasError = errors[id] && !loading[id];

                  return (
                    <button
                      key={id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveStrategy(id)}
                      className={`flex items-center gap-2 px-4 sm:px-5 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-white/85 backdrop-blur-md text-foreground shadow-sm ring-1 ring-white/70"
                          : "text-muted-foreground hover:bg-white/35 hover:text-foreground"
                      }`}
                    >
                      <Icon size={16} strokeWidth={2} />
                      <span>{label}</span>
                      {isLoading && (
                        <span className="text-xs font-normal tabular-nums">
                          {progress[id]}%
                        </span>
                      )}
                      {hasError && (
                        <span className="text-xs font-normal text-red-500">
                          Failed
                        </span>
                      )}
                    </button>
                  );
                })}
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
      {error && (
        <p className="text-white mt-2 text-center text-extrabold">{error}</p>
      )}
    </motion.div>
  );
};

export default AnalyzeForm;
